/**
 * @module convertNumber
 */
import { generateZeros, getNumberParts, ifNumberValidThen } from '../utils';
import { getServerNumberConvertRatio } from '../config';
import { CountryCode, CurrencyCode, ParsableNumber } from '../typings';

/**
 * Convert core server numbers to the number we use for calculation and display
 * @since  	2.0.0
 * @param 	{string|number} input
 * @param 	{?string} country
 * @param 	{?string} currencyId
 * @return  {*} Only valid number will be converted. If input is invalid, it will return the original input.
 * @example
 * deflateServerNumber(100000); // 1
 * deflateServerNumber('100000'); // 1
 * deflateServerNumber('100000', 'IR', 'IRR'); // 0.1
 */
export function deflateServerNumber(
  input: ParsableNumber,
  country?: CountryCode,
  currencyId?: CurrencyCode
): ParsableNumber {
  const ratio = getServerNumberConvertRatio(country, currencyId);
  const base = Math.log10(ratio);

  const { symbol, integer, decimal, decimalPoint } = getNumberParts(
    String(input)
  );
  if (integer.length > base) {
    const cutoff = integer.length - base;
    return (
      symbol +
      integer.slice(0, cutoff) +
      decimalPoint +
      integer.slice(cutoff) +
      (decimal || '')
    );
  } else {
    return (
      symbol +
      '0' +
      decimalPoint +
      generateZeros(base - integer.length) +
      integer +
      (decimal || '')
    );
  }
}

/**
 * Convert the number we use for calculation and display to core server numbers, reverse of deflateServerNumber
 * @since  	2.0.0
 * @param 	{string|number} input
 * @param 	{?CountryCode} country
 * @param 	{?CurrencyCode} currencyId
 * @return  {*} Only valid number will be converted. If input is invalid, it will return the original input.
 * @example
 * parseServerNumber(1); // 100000
 * parseServerNumber('1'); // 100000
 * parseServerNumber('1', 'IR', 'IRR'); // 1000000
 */
export function parseServerNumber(
  input: ParsableNumber,
  country?: CountryCode,
  currencyId?: CurrencyCode
): ParsableNumber {
  return ifNumberValidThen(input, (num) =>
    Math.round(num * getServerNumberConvertRatio(country, currencyId))
  );
}
